import { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { CurrencyInput } from './CurrencyInput';

const initialForm = {
    amount: '',
    reason: '',
    invoiceNumber: '',
    invoiceDate: '',
    carrier: '',
    billcomRef: '',
};

export const FreightDialog = ({
    open,
    poId,
    mode,
    initialAmount = '',
    onClose,
    onSubmitted,
}) => {
    const [form, setForm] = useState(initialForm);
    const [error, setError] = useState('');
    const [posting, setPosting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm({
            ...initialForm,
            amount: initialAmount == null ? '' : String(initialAmount),
        });
        setError('');
        setPosting(false);
    }, [open, initialAmount, mode]);

    if (!open) return null;

    const isEstimate = mode === 'estimate';
    const title = isEstimate ? 'Set freight estimate' : 'Enter freight invoice';
    const update = (name, value) => setForm((current) => ({ ...current, [name]: value }));

    const submit = async () => {
        const amount = Number(form.amount);
        if (!form.amount || !Number.isFinite(amount) || amount < 0 || (!isEstimate && amount <= 0)) {
            setError(isEstimate ? 'Enter a valid amount greater than or equal to zero.' : 'Enter a valid amount greater than zero.');
            return;
        }
        if (!isEstimate && !form.invoiceNumber.trim()) {
            setError('Invoice number is required.');
            return;
        }
        if (!isEstimate && !form.invoiceDate) {
            setError('Invoice date is required.');
            return;
        }
        setPosting(true);
        setError('');
        try {
            const url = isEstimate
                ? `/api/v1/po-ledger/${poId}/freight-estimate`
                : `/api/v1/po-ledger/${poId}/freight-invoice`;
            const payload = isEstimate
                ? {
                    amount: form.amount,
                    source: 'USER',
                    reason: form.reason.trim() || undefined,
                }
                : {
                    amount: form.amount,
                    invoice_number: form.invoiceNumber.trim(),
                    invoice_date: form.invoiceDate,
                    carrier: form.carrier.trim() || undefined,
                    billcom_ref: form.billcomRef.trim() || undefined,
                };
            await axios.post(url, payload);
            await onSubmitted?.();
            onClose();
        } catch (requestError) {
            setError(requestError.response?.data?.error || requestError.message || 'Unable to save freight entry.');
        } finally {
            setPosting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-start sm:items-center justify-center overflow-y-auto p-4" data-testid="freight-dialog">
            <div className="fixed inset-0 bg-gray-700 opacity-40" onClick={posting ? undefined : onClose} />
            <div className="relative z-[71] w-full max-w-md rounded-lg bg-white shadow-xl">
                <div className="p-5">
                    <div className="mb-4 flex items-center justify-between">
                        <h3 className="text-lg font-bold">{title}</h3>
                        <button type="button" onClick={onClose} disabled={posting} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </div>
                    <div className="space-y-3">
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-gray-700">Amount</label>
                            <CurrencyInput
                                value={form.amount}
                                onValueChange={(value) => update('amount', value)}
                                className="w-full bg-[#E8F0FE]"
                                autoFocus
                            />
                        </div>
                        {isEstimate ? (
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">Reason <span className="font-normal text-gray-500">(optional)</span></label>
                                <textarea
                                    value={form.reason}
                                    onChange={(event) => update('reason', event.target.value)}
                                    rows={2}
                                    className="w-full resize-y rounded-md border-gray-300 bg-[#E8F0FE] text-sm"
                                />
                            </div>
                        ) : (
                            <>
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-700">Invoice #</label>
                                    <input
                                        type="text"
                                        value={form.invoiceNumber}
                                        onChange={(event) => update('invoiceNumber', event.target.value)}
                                        className="w-full rounded-md border-gray-300 bg-[#E8F0FE] text-sm"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-700">Invoice date</label>
                                    <input
                                        type="date"
                                        value={form.invoiceDate}
                                        onChange={(event) => update('invoiceDate', event.target.value)}
                                        className="w-full rounded-md border-gray-300 bg-[#E8F0FE] text-sm"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-700">Carrier / forwarder</label>
                                    <input
                                        type="text"
                                        value={form.carrier}
                                        onChange={(event) => update('carrier', event.target.value)}
                                        className="w-full rounded-md border-gray-300 bg-[#E8F0FE] text-sm"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-700">Bill.com bill reference <span className="font-normal text-gray-500">(optional)</span></label>
                                    <input
                                        type="text"
                                        value={form.billcomRef}
                                        onChange={(event) => update('billcomRef', event.target.value)}
                                        className="w-full rounded-md border-gray-300 bg-[#E8F0FE] text-sm"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    {error && <div className="mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
                    <div className="mt-5 flex justify-end gap-2">
                        <button type="button" onClick={onClose} disabled={posting} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50">
                            Cancel
                        </button>
                        <button type="button" onClick={submit} disabled={posting} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50">
                            {posting ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
